import { useTranslation } from 'react-i18next';
import { ui } from '@procuros/datachecks';

type ChargesSection = Omit<ui.LineItemsSection, 'type'> & {
  type: 'charges';
};

type AllowancesSection = Omit<ui.LineItemsSection, 'type'> & {
  type: 'allowances';
};

type MatchedLineItemsSection = Omit<ui.LineItemsSection, 'type'> & {
  type: 'matched_line_items';
};
type UnmatchedLineItemsSection = Omit<ui.LineItemsSection, 'type'> & {
  type: 'unmatched_line_items';
};

export type InvoiceVerificationDetailsSection =
  | MatchedLineItemsSection
  | UnmatchedLineItemsSection
  | ChargesSection
  | AllowancesSection;

export const useUIConfig = (): Array<InvoiceVerificationDetailsSection> => {
  const { t } = useTranslation();
  return [
    {
      type: 'matched_line_items',
      fields: [
        {
          path: 'line_identifier',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.line_identifier'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'item.identifiers.gtin',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.item_identifiers_gtin'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'item.name',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.item_name'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'item.identifiers.buyer',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.item_identifiers_buyer'),
          is_readonly: false,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'item.identifiers.supplier',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.item_identifiers_supplier'),
          is_readonly: false,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'quantities.invoiced_quantity.value',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.quantities_invoiced_quantity_value'),
          is_readonly: false,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.number,
        },
        {
          path: 'item.prices.consolidated_unit_price.actual',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.item_prices_consolidated_unit_price_actual'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.monetary,
          type_config: {
            lookup_field: {
              type: 'relative_jsonata_expression',
              value: 'currency',
            },
          },
          jsonata_expression: '',
        },
        {
          path: 'item.taxes.actual.percentage',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.item_taxes_actual_percentage'),
          is_readonly: false,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.number,
          type_config: {
            suffix: '%',
          },
        },
        {
          path: 'due_amount.actual',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.due_amount_actual'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.monetary,
          type_config: {
            lookup_field: {
              type: 'relative_jsonata_expression',
              value: 'currency',
            },
          },
        },
      ],
    } as unknown as InvoiceVerificationDetailsSection,
    {
      type: 'unmatched_line_items',
      fields: [
        {
          path: 'line_identifier',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.line_identifier'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'item.identifiers.gtin',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.item_identifiers_gtin'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'item.name',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.item_name'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'item.identifiers.buyer',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.item_identifiers_buyer'),
          is_readonly: false,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'item.identifiers.supplier',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.item_identifiers_supplier'),
          is_readonly: false,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'order_identifier',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.order_identifier'),
          is_readonly: false,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'quantities.ordered_quantity.value',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.quantities_ordered_quantity_value'),
          is_readonly: false,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.number,
        },
        {
          path: 'quantities.accepted_quantity.value',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.quantities_accepted_quantity_value'),
          is_readonly: false,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.number,
        },
        {
          path: 'item.prices.consolidated_unit_price.expected',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.item_prices_consolidated_unit_price_expected'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.monetary,
          type_config: {
            lookup_field: {
              type: 'relative_jsonata_expression',
              value: 'currency',
            },
          },
          jsonata_expression: '',
        },
        {
          path: 'item.taxes.expected.percentage',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.line_items.item_taxes_expected_percentage'),
          is_readonly: false,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.number,
          type_config: {
            suffix: '%',
          },
        },
      ],
    } as unknown as InvoiceVerificationDetailsSection,
    {
      type: 'charges',
      fields: [
        {
          path: 'description',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.charges.description'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'expected.amount',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.charges.expected_amount'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.monetary,
          type_config: {
            lookup_field: {
              type: 'relative_jsonata_expression',
              value: 'currency',
            },
          },
        },
        {
          path: 'actual.amount',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.charges.actual_amount'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.monetary,
          type_config: {
            lookup_field: {
              type: 'relative_jsonata_expression',
              value: 'currency',
            },
          },
        },
      ],
    } as unknown as InvoiceVerificationDetailsSection,
    {
      type: 'allowances',
      fields: [
        {
          path: 'description',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.allowances.description'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.text,
        },
        {
          path: 'expected.amount',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.allowances.expected_amount'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.monetary,
          type_config: {
            lookup_field: {
              type: 'relative_jsonata_expression',
              value: 'currency',
            },
          },
        },
        {
          path: 'actual.amount',
          modality: 'OPTIONAL',
          label: t('invoiceVerification:detailsPage.headers.allowances.actual_amount'),
          is_readonly: true,
          is_hidden: false,
          is_virtual: false,
          type: ui.FieldTypes.monetary,
          type_config: {
            lookup_field: {
              type: 'relative_jsonata_expression',
              value: 'currency',
            },
          },
        },
      ],
    } as unknown as InvoiceVerificationDetailsSection,
  ];
};
